
import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Search,
  Loader2,
  Globe,
  Building2,
  DollarSign,
  MapPin,
  ExternalLink,
  Sparkles,
  UserPlus,
  BookmarkPlus,
} from "lucide-react"
import { IreneLogoMark } from "@/components/irene-logo"
import { cn } from "@/lib/utils"
import { useIntercom } from "@/hooks/use-intercom"
import { saveSearch, createInvestor, createActivity } from "@/lib/api-client"

const API_BASE = "https://irene-api.heysalad.app"

function getAuthToken(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|; )irene_token=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

interface InvestorResult {
  name: string
  firm: string
  title: string
  location: string
  focusAreas: string[]
  checkSize: string
  stage: string
  thesis: string
  website: string
  relevanceScore: number
  reasoning: string
  recentActivity: string
}

interface SearchStatus {
  phase: string
  detail: string
}

export default function InvestorSearchPage() {
  const [query, setQuery] = useState("")
  const [industry, setIndustry] = useState("")
  const [stage, setStage] = useState("")
  const [geography, setGeography] = useState("")
  const [context, setContext] = useState("")
  const [results, setResults] = useState<InvestorResult[]>([])
  const { trackInvestorSearched } = useIntercom()
  const [isSearching, setIsSearching] = useState(false)
  const [searchStatus, setSearchStatus] = useState<SearchStatus | null>(null)
  const [savedInvestors, setSavedInvestors] = useState<Set<string>>(new Set())
  const [searchComplete, setSearchComplete] = useState(false)
  const [aiSummary, setAiSummary] = useState("")
  const [showFilters, setShowFilters] = useState(false)

  const hasAnyInput = Boolean(query.trim() || industry || stage || geography || context.trim())
  const filledFilterCount = [industry, stage, geography, context.trim()].filter(Boolean).length

  const clearFilters = () => {
    setIndustry("")
    setStage("")
    setGeography("")
    setContext("")
  }

  const handleSearch = async () => {
    setIsSearching(true)
    setResults([])
    setSearchComplete(false)
    setAiSummary("")

    const phases = [
      { phase: "Searching", detail: "Querying investor databases and public sources via Tavily..." },
      { phase: "Deep scraping", detail: "Using Browserless to extract detailed data from top sources..." },
      { phase: "Analyzing", detail: "Irene is evaluating investor-founder fit with enriched data..." },
      { phase: "Ranking", detail: "Scoring relevance and preparing recommendations..." },
    ]

    for (const p of phases) {
      setSearchStatus(p)
      await new Promise((r) => setTimeout(r, 800))
    }

    try {
      const token = getAuthToken()
      if (!token) {
        throw new Error("You must be logged in to search. Please sign in and try again.")
      }
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      }

      const res = await fetch(`${API_BASE}/api/search-investors`, {
        method: "POST",
        headers,
        mode: "cors",
        body: JSON.stringify({
          query: query || `${industry} investors`,
          industry,
          stage,
          geography,
          context,
        }),
      })

      const json = await res.json().catch(() => null) as any

      if (!res.ok) {
        const errorMsg = json?.error || json?.message || `Search failed with status ${res.status}`
        if (res.status === 401) {
          throw new Error("Session expired. Please log in again.")
        }
        if (res.status === 429) {
          throw new Error(json?.error || "Rate limit exceeded. Please wait a moment and try again.")
        }
        throw new Error(errorMsg)
      }

      if (!json) {
        throw new Error("Empty response from server. Please try again.")
      }

      // Backend wraps response as { success, data: { investors, summary } }
      const data = json.data || json
      const investorResults = data.investors || []
      setResults(investorResults)
      setAiSummary(data.summary || "")
      trackInvestorSearched({
        query,
        industry,
        stage,
        resultCount: investorResults.length,
      })

      // Save search to API
      try {
        await saveSearch({
          query: query || `${industry} investors`,
          filters: { industry, stage, geography },
          results_count: investorResults.length,
        })
      } catch {
        // Non-critical
      }
    } catch (err) {
      setResults([])
      setAiSummary(err instanceof Error ? err.message : "Search encountered an unknown error. Please try again.")
    }

    setIsSearching(false)
    setSearchStatus(null)
    setSearchComplete(true)
  }

  const toggleSave = async (name: string) => {
    const investor = results.find((r) => r.name === name)
    setSavedInvestors((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })

    // If saving (not unsaving), create investor in CRM
    if (investor && !savedInvestors.has(name)) {
      try {
        await createInvestor({
          name: investor.name,
          firm: investor.firm,
          stage: "Lead",
          type: "data-driven",
          investment_thesis: investor.thesis,
          tags: investor.focusAreas,
        })
        await createActivity({
          type: "note",
          title: `Added ${investor.name} from AI search`,
          description: `Found via search: "${query}". Match score: ${investor.relevanceScore}%`,
        })
      } catch {
        // Non-critical
      }
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 85) return "text-green-600 bg-green-50 border-green-200"
    if (score >= 70) return "text-amber-600 bg-amber-50 border-amber-200"
    return "text-muted-foreground bg-muted border-border"
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-sans text-2xl font-bold text-foreground">
            AI Investor Search
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Let Irene autonomously find and evaluate the best investors for your raise
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2">
          <IreneLogoMark size={18} />
          <span className="text-xs font-medium text-primary">Powered by Irene</span>
        </div>
      </div>

      {/* Search Configuration */}
      <Card className="mt-6 border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="h-4 w-4 text-primary" />
            Search Parameters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-5">
            {/* Main query — primary input */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="query">Describe what you're looking for</Label>
              <Textarea
                id="query"
                placeholder="e.g. VCs who invest in FoodTech Series A in Southeast Asia, angel investors in B2B SaaS, climate-focused funds..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                rows={2}
                className="resize-none text-sm"
              />
              <p className="text-xs text-muted-foreground">
                This is the only required field. Use the filters below to narrow results.
              </p>
            </div>

            {/* Optional filters — collapsed feel */}
            <div>
              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={cn("h-3.5 w-3.5 transition-transform", showFilters && "rotate-90")}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                Optional filters
                {filledFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                    {filledFilterCount} active
                  </Badge>
                )}
              </button>

              {showFilters && (
                <div className="mt-4 flex flex-col gap-4 rounded-xl border border-border bg-muted/20 p-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-xs text-muted-foreground">Industry</Label>
                      <Select value={industry} onValueChange={setIndustry}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Any industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AI / Machine Learning">AI / ML</SelectItem>
                          <SelectItem value="B2B SaaS">B2B SaaS</SelectItem>
                          <SelectItem value="Consumer">Consumer</SelectItem>
                          <SelectItem value="FinTech">FinTech</SelectItem>
                          <SelectItem value="FoodTech">FoodTech</SelectItem>
                          <SelectItem value="HealthTech">HealthTech</SelectItem>
                          <SelectItem value="Climate">Climate / CleanTech</SelectItem>
                          <SelectItem value="E-Commerce">E-Commerce</SelectItem>
                          <SelectItem value="Developer Tools">Developer Tools</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-xs text-muted-foreground">Stage</Label>
                      <Select value={stage} onValueChange={setStage}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Any stage" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pre-Seed">Pre-Seed</SelectItem>
                          <SelectItem value="Seed">Seed</SelectItem>
                          <SelectItem value="Series A">Series A</SelectItem>
                          <SelectItem value="Series B">Series B</SelectItem>
                          <SelectItem value="Series C+">Series C+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-xs text-muted-foreground">Geography</Label>
                      <Select value={geography} onValueChange={setGeography}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Any region" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US - Silicon Valley">US - Silicon Valley</SelectItem>
                          <SelectItem value="US - New York">US - New York</SelectItem>
                          <SelectItem value="Europe">Europe</SelectItem>
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="Southeast Asia">Southeast Asia</SelectItem>
                          <SelectItem value="India">India</SelectItem>
                          <SelectItem value="Global">Global</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Additional context */}
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">Extra context for Irene</Label>
                    <Textarea
                      placeholder="e.g. We do $15k MRR, looking for investors who backed Deliveroo, DoorDash..."
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      rows={2}
                      className="resize-none text-sm"
                    />
                  </div>

                  {filledFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {hasAnyInput
                  ? "Ready to search"
                  : "Fill in at least one field to search"}
              </p>
              <Button
                onClick={handleSearch}
                disabled={isSearching || !hasAnyInput}
                className="gap-2"
              >
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {isSearching ? "Searching..." : "Find Investors"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search progress */}
      {isSearching && searchStatus && (
        <Card className="mt-6 border-primary/20 bg-primary/5">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary">
              <Loader2 className="h-5 w-5 animate-spin text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{searchStatus.phase}</p>
              <p className="text-xs text-muted-foreground">{searchStatus.detail}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Summary */}
      {searchComplete && aiSummary && (
        <Card className="mt-6 border-primary/20 bg-gradient-to-r from-primary/5 via-secondary/5 to-muted/50">
          <CardContent className="flex items-start gap-3 p-5">
            <IreneLogoMark size={28} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Irene{"'"}s Analysis</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
                {aiSummary}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {searchComplete && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="font-sans text-lg font-semibold text-foreground">
              {results.length > 0
                ? `${results.length} Investor${results.length === 1 ? "" : "s"} Found`
                : "No Results"}
            </h2>
            {savedInvestors.size > 0 && (
              <Badge variant="secondary" className="gap-1">
                <BookmarkPlus className="h-3 w-3" />
                {savedInvestors.size} saved
              </Badge>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-4">
            {results.map((investor) => (
              <Card
                key={`${investor.name}-${investor.firm}`}
                className={cn(
                  "border-border transition-shadow hover:shadow-md",
                  savedInvestors.has(investor.name) && "border-primary/30 bg-primary/[0.02]"
                )}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{investor.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {investor.title} at {investor.firm}
                          </p>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        {investor.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {investor.location}
                          </span>
                        )}
                        {investor.checkSize && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {investor.checkSize}
                          </span>
                        )}
                        {investor.stage && (
                          <Badge variant="outline" className="text-xs">
                            {investor.stage}
                          </Badge>
                        )}
                      </div>

                      {/* Focus areas */}
                      {investor.focusAreas.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {investor.focusAreas.map((area) => (
                            <Badge
                              key={area}
                              variant="secondary"
                              className="text-xs font-normal"
                            >
                              {area}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Thesis */}
                      {investor.thesis && (
                        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                          {investor.thesis}
                        </p>
                      )}

                      {/* AI reasoning */}
                      {investor.reasoning && (
                        <div className="mt-3 flex items-start gap-2 rounded-lg bg-muted/50 p-3">
                          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                          <p className="text-xs leading-relaxed text-muted-foreground">
                            <span className="font-medium text-foreground">Why this match: </span>
                            {investor.reasoning}
                          </p>
                        </div>
                      )}

                      {/* Recent activity */}
                      {investor.recentActivity && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          <span className="font-medium">Recent: </span>
                          {investor.recentActivity}
                        </p>
                      )}
                    </div>

                    {/* Right: Score + Actions */}
                    <div className="flex shrink-0 flex-col items-end gap-3">
                      <div
                        className={cn(
                          "flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold",
                          getScoreColor(investor.relevanceScore)
                        )}
                      >
                        {investor.relevanceScore}% match
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant={savedInvestors.has(investor.name) ? "default" : "outline"}
                          className="gap-1.5 text-xs"
                          onClick={() => toggleSave(investor.name)}
                        >
                          {savedInvestors.has(investor.name) ? (
                            <>
                              <Check className="h-3 w-3" />
                              Saved
                            </>
                          ) : (
                            <>
                              <UserPlus className="h-3 w-3" />
                              Save
                            </>
                          )}
                        </Button>
                        {investor.website && (
                          <Button size="sm" variant="ghost" className="gap-1.5 text-xs" asChild>
                            <a href={investor.website} target="_blank" rel="noopener noreferrer">
                              <Globe className="h-3 w-3" />
                              Website
                              <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Check({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
